// assetAllocationPieChart.tsx

import { useGraphColors } from '@/hooks/useGraphColors';
import {
  DiversificationItem,
  DiversificationResponse,
  useGetDiversificationCategoriesQuery,
  useGetDiversificationSchemesQuery,
  useGetAccountDiversificationQuery,
  useGetDiversificationQuery,
} from '@/store/diversificationsApi';
import { formatNumberToCurrency } from '@/utils/currency';
import {
  adaptiveCategoricalTickInterval,
  getCurrencyStringTitleForChart,
} from '@/utils/charts/axisFormatters';
import { useViewportWidth } from '@/hooks/useViewportWidth';
import { formatUtcDate } from '@/utils/date';
import { convertNumberToPercentage, formatPercentValue } from '@/utils/numbers';
import ArrowBack from '@mui/icons-material/ArrowBack';
import NavigateNext from '@mui/icons-material/NavigateNext';
import {
  Box,
  Breadcrumbs,
  useMediaQuery,
  Button,
  Card,
  CardContent,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { HighlightItemData } from '@mui/x-charts/context';
import AssetAllocationHoldingsGrid from './assetAllocationHoldingsGrid';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PieChartWithLegend from '@/components/common/charts/PieChartWithLegend';
import TwoPieChartsSkeleton from '@/components/common/charts/TwoPieChartsSkeleton';
import { mobileWidth } from '@/constants';
import { PortfolioDiversificationIdNumber } from '@/utils/enums';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { useLocation } from 'react-router';
import {
  buildSchemeNameToIdMap,
  buildDiversificationIdToCategoryIdMap,
  getDrilldownDiversificationId,
  pickAccountRootDiversificationData,
  recordHasDiversificationDrilldown,
  resolveAccountRootDrilldownId,
  resolveDrilldownCategoryId,
  shouldSwitchBarLevelToHoldings,
} from './assetAllocationDrilldownUtils';
import { useLocalization } from '@/hooks/useLocalization';

import {
  AssetAllocationBarChartTooltip,
  BarChartDataContext,
} from './tooltips/AssetAllocationBarChartTooltip';

/** Accounts with no asset class. Kept on the pie even when under the Other cutoff. */
const UNCLASSIFIED_SLICE_LABEL = 'Unclassified';
const ALLOCATION_KEEP_VISIBLE_LABELS = [UNCLASSIFIED_SLICE_LABEL];

/** Fixed content width so pie, bar, and holdings views do not shift horizontally when switching. */
const CHART_CONTENT_WIDTH = 1200;
const CHART_AREA_HEIGHT = 400;
const chartContentRootSx = {
  width: '100%',
  maxWidth: CHART_CONTENT_WIDTH,
  mx: 'auto',
  minWidth: 0,
} as const;

/** Params passed to overrideData to request mock data for a given level (e.g. Storybook). */
export interface AssetAllocationOverrideParams {
  accountId?: number;
  diversificationId?: number;
  categoryId?: number | null;
}

interface AssetAllocationPieChartProps {
  asOfDate: Date;
  accountId?: number;
  investorId?: number;
  investorScoped?: boolean;
  /**
   * Suppresses the holding detail flyout, whose queries are not investor-scoped.
   * Set by the advisor mount until that chain carries `investorId`.
   */
  disableHoldingDetails?: boolean;
  /**
   * Forces the chart + skeleton into XS/mobile layout even on desktop viewports.
   * Used by narrow dashboard widgets where container width drives layout.
   */
  forceMobile?: boolean;
  onDataLoaded?: () => void;
  /** Notifies parent when chart data is loading or refetching (e.g. to disable widget date pickers). */
  onChartUpdatingChange?: (isUpdating: boolean) => void;
  /** When provided, used instead of API for the current level (e.g. Storybook). */
  overrideData?: (
    params: AssetAllocationOverrideParams,
  ) => DiversificationResponse | undefined;
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
  /** Set by dashboard tiles so the drilled-down holdings grid gets the widget height instead of the full-page one. */
  isWidget?: boolean;
  /** Leaves out the Target pie even when the response carries targets. */
  hideTarget?: boolean;
  /** Adds a Retry button to the error state, as the advisor widgets have. */
  showRetry?: boolean;
  /** Shows the no-data text instead of an empty pie when there is nothing to chart. */
  showNoDataMessage?: boolean;
}

/** One request the chart can retry: its arguments, whether it failed, and how to re-send it. */
interface RetryableRequest {
  args: object | typeof skipToken;
  isError: boolean;
  refetch: () => void;
}

interface DrillDownLevel {
  type: 'pie' | 'bar' | 'holdings';
  title: string;
  diversificationId?: number;
  categoryId?: number | null;
  data?: DiversificationItem[];
}

export interface BarChartDatum {
  name: string;
  value: number;
  color: string;
  originalRecord: DiversificationItem;
}

interface AccountDrillDownQueryState {
  data: DiversificationResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

function asQueryBoolean(value: unknown): boolean {
  return value === true;
}

/** RTK `skipToken` widens query `data` to `any`; narrow at the chart boundary. */
function asDiversificationResponse(
  data: unknown,
): DiversificationResponse | undefined {
  if (
    typeof data === 'object' &&
    data !== null &&
    'Records' in data &&
    Array.isArray((data as DiversificationResponse).Records)
  ) {
    return data as DiversificationResponse;
  }
  return undefined;
}

function accountRootHasRecords(
  grouped: DiversificationResponse | undefined,
  ungrouped: DiversificationResponse | undefined,
): boolean {
  return (
    (grouped?.Records.length ?? 0) > 0 || (ungrouped?.Records.length ?? 0) > 0
  );
}

// Skeleton components for loading state

const BarChartSkeleton = () => (
  <Stack
    direction='column'
    gap={4}
    alignItems='center'
  >
    <Box
      width='100%'
      maxWidth='1000px'
      mx='auto'
    >
      <Skeleton
        variant='text'
        width='40%'
        sx={{ mx: 'auto', mb: 1 }}
      />
      <Skeleton
        variant='text'
        width='30%'
        sx={{ mx: 'auto', mb: 2 }}
      />
      <Skeleton
        variant='rectangular'
        height={CHART_AREA_HEIGHT}
        sx={{ borderRadius: 2 }}
      />
      <Stack
        spacing={1}
        mt={2}
      >
        {Array.from({ length: 3 }, (_, index) => (
          <Box
            key={index}
            display='flex'
            justifyContent='space-between'
          >
            <Skeleton
              variant='rectangular'
              width='60%'
              height={24}
            />
            <Skeleton
              variant='rectangular'
              width='25%'
              height={24}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  </Stack>
);

const AssetAllocationPieChart: React.FC<AssetAllocationPieChartProps> = ({
  asOfDate,
  accountId,
  investorId,
  investorScoped = false,
  disableHoldingDetails = false,
  forceMobile = false,
  onDataLoaded,
  onChartUpdatingChange,
  overrideData,
  excludedAccounts,
  excludedCategories,
  excludedAccountTypes,
  excludedInstitutions,
  isWidget = false,
  hideTarget = false,
  showRetry = false,
  showNoDataMessage = false,
}) => {
  const { t } = useLocalization();
  const asOfDateString = formatUtcDate(asOfDate);
  const todayAsOfDateString = formatUtcDate(new Date());
  const isMobileMatch = useMediaQuery(mobileWidth);
  const shouldUseMobileLayout = forceMobile || isMobileMatch;
  const graphColors = useGraphColors();
  const effectiveExclusions = useEffectiveExclusions({
    excludedAccounts,
    excludedCategories,
    excludedAccountTypes,
    excludedInstitutions,
    institutionsHandledByServer: investorScoped,
  });
  const location = useLocation();
  const viewportWidth = useViewportWidth();

  // Use the full scheme list for name→id mapping.
  const { data: schemesData } = useGetDiversificationSchemesQuery();
  const { data: diversificationCategoriesData } =
    useGetDiversificationCategoriesQuery();

  const [drillDownStack, setDrillDownStack] = useState<DrillDownLevel[]>([
    { type: 'pie', title: t('Asset Allocation') },
  ]);
  const lastAdjustedLevelRef = useRef<string | null>(null);

  // Controlled highlighting states
  const [currentHighlightedItem, setCurrentHighlightedItem] =
    useState<HighlightItemData | null>(null);
  const [targetHighlightedItem, setTargetHighlightedItem] =
    useState<HighlightItemData | null>(null);

  const currentLevel = drillDownStack[drillDownStack.length - 1];

  // If the chart context changes (switching between portfolio and account view, or changing dates),
  // reset the drill-down stack so we don't carry stale diversification/category ids into new queries.
  useEffect(() => {
    setDrillDownStack([{ type: 'pie', title: t('Asset Allocation') }]);
    lastAdjustedLevelRef.current = null;
    setCurrentHighlightedItem(null);
    setTargetHighlightedItem(null);
  }, [accountId, investorId, asOfDateString, location.pathname, t]);

  // Defensive: the Accounts page uses tabs and can keep this component mounted.
  // If we land on the Accounts route with an accountId but a stale drilled state,
  // force-reset so the account chart starts at the root pie.
  const accountsForceResetKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (accountId === undefined) return;
    if (!location.pathname.includes('/Accounts')) return;
    const key = `${location.pathname}|${String(accountId)}|${asOfDateString}`;
    if (accountsForceResetKeyRef.current === key) return;
    accountsForceResetKeyRef.current = key;
    if (drillDownStack.length <= 1) return;
    setDrillDownStack([{ type: 'pie', title: t('Asset Allocation') }]);
    lastAdjustedLevelRef.current = null;
    setCurrentHighlightedItem(null);
    setTargetHighlightedItem(null);
  }, [
    accountId,
    location.pathname,
    asOfDateString,
    drillDownStack.length,
    currentLevel,
    t,
  ]);

  const overrideParams: AssetAllocationOverrideParams =
    currentLevel.type === 'pie'
      ? accountId !== undefined
        ? { accountId }
        : { diversificationId: PortfolioDiversificationIdNumber.ROOT }
      : {
          diversificationId: currentLevel.diversificationId,
          categoryId: currentLevel.categoryId,
        };
  const overrideResult: DiversificationResponse | undefined =
    overrideData?.(overrideParams);
  const usingOverrideData = overrideResult !== undefined;

  const skipPortfolio =
    Boolean(overrideData) &&
    currentLevel.type === 'pie' &&
    accountId === undefined &&
    overrideResult !== undefined;
  const skipAccount =
    Boolean(overrideData) &&
    currentLevel.type === 'pie' &&
    accountId !== undefined &&
    overrideResult !== undefined;
  const skipDrillDown =
    Boolean(overrideData) &&
    currentLevel.type !== 'pie' &&
    overrideResult !== undefined;

  const portfolioArgs =
    skipPortfolio || accountId !== undefined || currentLevel.type !== 'pie'
      ? skipToken
      : {
          asOfDate: asOfDateString,
          diversificationId: PortfolioDiversificationIdNumber.ROOT,
          ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
          ...(!investorScoped && effectiveExclusions.excludedAccounts.length > 0
            ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
            : {}),
          ...(effectiveExclusions.excludedAccountTypes.length > 0
            ? { ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes }
            : {}),
          ...(effectiveExclusions.excludedCategories.length > 0
            ? { ExcludedCategories: effectiveExclusions.excludedCategories }
            : {}),
          ...(investorScoped &&
          effectiveExclusions.excludedInstitutions.length > 0
            ? { ExcludedInstitutions: effectiveExclusions.excludedInstitutions }
            : {}),
        };

  const accountArgs =
    !skipAccount && accountId !== undefined && currentLevel.type === 'pie'
      ? {
          accountId,
          asOfDate: asOfDateString,
          diversificationId: PortfolioDiversificationIdNumber.ROOT,
          groupBySubClassification: true,
        }
      : skipToken;

  // Manual accounts can return rows only when grouped by sub-classification.
  // Prefer the ungrouped saved allocation when it has rows.
  const accountArgsUngrouped =
    !skipAccount && accountId !== undefined && currentLevel.type === 'pie'
      ? {
          accountId,
          asOfDate: asOfDateString,
          diversificationId: PortfolioDiversificationIdNumber.ROOT,
          groupBySubClassification: false,
        }
      : skipToken;

  const accountDrilldownUsesAsOfDate = asOfDateString === todayAsOfDateString;

  // The next AccountDiversification response is shown as returned. A manual
  // account slice comes back as one closed row for that account. Do not group
  // that row again or add the account balance on top of it.
  const accountDrillDownArgs =
    !skipDrillDown &&
    accountId !== undefined &&
    currentLevel.type !== 'pie' &&
    currentLevel.diversificationId !== undefined &&
    accountDrilldownUsesAsOfDate
      ? {
          accountId,
          asOfDate: asOfDateString,
          diversificationId: currentLevel.diversificationId,
          ...(currentLevel.categoryId != null
            ? { categoryId: currentLevel.categoryId }
            : {}),
        }
      : skipToken;

  const accountDrillDownArgsNoAsOfDate =
    !skipDrillDown &&
    accountId !== undefined &&
    currentLevel.type !== 'pie' &&
    currentLevel.diversificationId !== undefined &&
    !accountDrilldownUsesAsOfDate
      ? {
          accountId,
          diversificationId: currentLevel.diversificationId,
          ...(currentLevel.categoryId != null
            ? { categoryId: currentLevel.categoryId }
            : {}),
        }
      : skipToken;

  const drillDownArgs =
    !skipDrillDown &&
    accountId === undefined &&
    currentLevel.type !== 'pie' &&
    currentLevel.diversificationId !== undefined
      ? {
          asOfDate: asOfDateString,
          categoryId: currentLevel.categoryId,
          diversificationId: currentLevel.diversificationId,
          ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
          ...(!investorScoped && effectiveExclusions.excludedAccounts.length > 0
            ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
            : {}),
          ...(effectiveExclusions.excludedAccountTypes.length > 0
            ? { ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes }
            : {}),
          ...(effectiveExclusions.excludedCategories.length > 0
            ? { ExcludedCategories: effectiveExclusions.excludedCategories }
            : {}),
          ...(investorScoped &&
          effectiveExclusions.excludedInstitutions.length > 0
            ? { ExcludedInstitutions: effectiveExclusions.excludedInstitutions }
            : {}),
        }
      : skipToken;

  const {
    data: portfolioData,
    isLoading: isPortfolioLoading,
    isFetching: isPortfolioFetching,
    isError: isPortfolioError,
    refetch: refetchPortfolio,
  } = useGetDiversificationQuery(portfolioArgs);

  const {
    data: accountData,
    isLoading: isAccountLoading,
    isFetching: isAccountFetching,
    isError: isAccountError,
    refetch: refetchAccount,
  } = useGetAccountDiversificationQuery(accountArgs);

  const {
    data: accountDataUngrouped,
    isLoading: isAccountUngroupedLoading,
    isFetching: isAccountUngroupedFetching,
    isError: isAccountUngroupedError,
    refetch: refetchAccountUngrouped,
  } = useGetAccountDiversificationQuery(accountArgsUngrouped);

  const {
    data: accountDrillDownData,
    isLoading: isAccountDrillDownLoading,
    isFetching: isAccountDrillDownFetching,
    isError: isAccountDrillDownError,
    refetch: refetchAccountDrillDown,
  } = useGetAccountDiversificationQuery(accountDrillDownArgs);

  const {
    data: accountDrillDownDataNoAsOfDate,
    isLoading: isAccountDrillDownNoAsOfDateLoading,
    isFetching: isAccountDrillDownNoAsOfDateFetching,
    isError: isAccountDrillDownNoAsOfDateError,
    refetch: refetchAccountDrillDownNoAsOfDate,
  } = useGetAccountDiversificationQuery(accountDrillDownArgsNoAsOfDate);

  const {
    data: drillDownData,
    isLoading: isDrillDownLoading,
    isFetching: isDrillDownFetching,
    isError: isDrillDownError,
    refetch: refetchDrillDown,
  } = useGetDiversificationQuery(drillDownArgs);

  const accountDrillDownPicked = useMemo((): AccountDrillDownQueryState => {
    if (accountDrilldownUsesAsOfDate) {
      return {
        data: accountDrillDownData,
        isLoading: isAccountDrillDownLoading,
        isFetching: isAccountDrillDownFetching,
        isError: isAccountDrillDownError,
      };
    }
    return {
      data: accountDrillDownDataNoAsOfDate,
      isLoading: isAccountDrillDownNoAsOfDateLoading,
      isFetching: isAccountDrillDownNoAsOfDateFetching,
      isError: isAccountDrillDownNoAsOfDateError,
    };
  }, [
    accountDrilldownUsesAsOfDate,
    accountDrillDownData,
    accountDrillDownDataNoAsOfDate,
    isAccountDrillDownLoading,
    isAccountDrillDownFetching,
    isAccountDrillDownError,
    isAccountDrillDownNoAsOfDateLoading,
    isAccountDrillDownNoAsOfDateFetching,
    isAccountDrillDownNoAsOfDateError,
  ]);

  // Determine which data and status to use (override takes precedence)
  const apiData: DiversificationResponse | undefined =
    overrideResult ??
    (currentLevel.type === 'pie'
      ? accountId !== undefined
        ? pickAccountRootDiversificationData(
            asDiversificationResponse(accountData),
            asDiversificationResponse(accountDataUngrouped),
          )
        : asDiversificationResponse(portfolioData)
      : accountId !== undefined
        ? accountDrillDownPicked.data
        : asDiversificationResponse(drillDownData));

  const isLoading: boolean = usingOverrideData
    ? false
    : currentLevel.type === 'pie'
      ? accountId !== undefined
        ? asQueryBoolean(isAccountLoading) ||
          asQueryBoolean(isAccountUngroupedLoading)
        : asQueryBoolean(isPortfolioLoading)
      : accountId !== undefined
        ? asQueryBoolean(accountDrillDownPicked.isLoading)
        : asQueryBoolean(isDrillDownLoading);

  const isFetching: boolean = usingOverrideData
    ? false
    : currentLevel.type === 'pie'
      ? accountId !== undefined
        ? asQueryBoolean(isAccountFetching) ||
          asQueryBoolean(isAccountUngroupedFetching)
        : asQueryBoolean(isPortfolioFetching)
      : accountId !== undefined
        ? asQueryBoolean(accountDrillDownPicked.isFetching)
        : asQueryBoolean(isDrillDownFetching);

  const isChartUpdating: boolean = isLoading || isFetching;

  useEffect(() => {
    onChartUpdatingChange?.(isChartUpdating);
  }, [isChartUpdating, onChartUpdatingChange]);

  const isError: boolean = usingOverrideData
    ? false
    : currentLevel.type === 'pie'
      ? accountId !== undefined
        ? !accountRootHasRecords(
            asDiversificationResponse(accountData),
            asDiversificationResponse(accountDataUngrouped),
          ) &&
          asQueryBoolean(isAccountError) &&
          asQueryBoolean(isAccountUngroupedError)
        : asQueryBoolean(isPortfolioError)
      : accountId !== undefined
        ? asQueryBoolean(accountDrillDownPicked.isError)
        : asQueryBoolean(isDrillDownError);

  const retryableRequests: RetryableRequest[] = [
    {
      args: portfolioArgs,
      isError: asQueryBoolean(isPortfolioError),
      refetch: () => {
        void refetchPortfolio();
      },
    },
    {
      args: accountArgs,
      isError: asQueryBoolean(isAccountError),
      refetch: () => {
        void refetchAccount();
      },
    },
    {
      args: accountArgsUngrouped,
      isError: asQueryBoolean(isAccountUngroupedError),
      refetch: () => {
        void refetchAccountUngrouped();
      },
    },
    {
      args: accountDrillDownArgs,
      isError: asQueryBoolean(isAccountDrillDownError),
      refetch: () => {
        void refetchAccountDrillDown();
      },
    },
    {
      args: accountDrillDownArgsNoAsOfDate,
      isError: asQueryBoolean(isAccountDrillDownNoAsOfDateError),
      refetch: () => {
        void refetchAccountDrillDownNoAsOfDate();
      },
    },
    {
      args: drillDownArgs,
      isError: asQueryBoolean(isDrillDownError),
      refetch: () => {
        void refetchDrillDown();
      },
    },
  ];

  /** Re-sends the requests behind the current level that failed. */
  const retryFailedRequests = () => {
    retryableRequests
      .filter((request) => request.args !== skipToken && request.isError)
      .forEach((request) => {
        request.refetch();
      });
  };

  useEffect(() => {
    if (!isLoading && !isFetching && apiData) {
      onDataLoaded?.();
    }
  }, [isLoading, isFetching, apiData, onDataLoaded]);

  const portfolioRecords = useMemo(
    (): DiversificationItem[] => apiData?.Records ?? [],
    [apiData],
  );

  // Target comparison comes from the saved allocation or the tag diversification
  // response. Grouped sub-classification rows are not part of that comparison.
  const targetRecords = useMemo((): DiversificationItem[] => {
    if (overrideResult) return overrideResult.Records;
    if (currentLevel.type !== 'pie') return [];
    if (accountId !== undefined) {
      return asDiversificationResponse(accountDataUngrouped)?.Records ?? [];
    }
    return asDiversificationResponse(portfolioData)?.Records ?? [];
  }, [
    overrideResult,
    currentLevel.type,
    accountId,
    accountDataUngrouped,
    portfolioData,
  ]);

  const schemeRootNameToId = useMemo(
    () => buildSchemeNameToIdMap(schemesData?.Records),
    [schemesData],
  );
  const schemeDiversificationIdToCategoryId = useMemo(
    () => buildDiversificationIdToCategoryIdMap(schemesData?.Records),
    [schemesData],
  );

  const currentValues = useMemo(() => {
    const dataMap: Record<string, number> = {};
    portfolioRecords.forEach((item) => {
      if (item.name && typeof item.y === 'number') {
        if (!dataMap[item.name]) dataMap[item.name] = 0;
        dataMap[item.name] += item.y;
      }
    });
    return dataMap;
  }, [portfolioRecords]);

  const currentPieData = useMemo(() => {
    const total = Object.values(currentValues).reduce((a, b) => a + b, 0);
    return Object.entries(currentValues).map(([name, value]) => {
      const originalRecord = portfolioRecords.find((r) => r.name === name);
      return {
        id: `current-${name}`,
        label: name,
        value,
        formattedValue: formatNumberToCurrency(value),
        percentageLabel:
          total > 0 ? convertNumberToPercentage(value / total) : '0%',
        originalRecord,
      };
    });
  }, [currentValues, portfolioRecords]);

  const currentItems = useMemo(
    () =>
      currentPieData.map((d) => ({
        label: d.label,
        amount: d.value,
        original: d.originalRecord,
      })),
    [currentPieData],
  );

  const currentTotalValue = useMemo(
    () => Object.values(currentValues).reduce((a, b) => a + b, 0),
    [currentValues],
  );

  const hasTargetData = useMemo(
    () =>
      targetRecords.some(
        (item) =>
          typeof item.TargetAmount === 'number' ||
          typeof item.TargetPercent === 'number',
      ),
    [targetRecords],
  );

  const showTarget = hasTargetData && !hideTarget;

  const targetValues = useMemo(() => {
    if (!hasTargetData) return {};
    const dataMap: Record<string, number> = {};
    targetRecords.forEach((item) => {
      if (item.name && typeof item.TargetAmount === 'number') {
        if (!dataMap[item.name]) dataMap[item.name] = 0;
        dataMap[item.name] += item.TargetAmount;
      }
    });
    return dataMap;
  }, [targetRecords, hasTargetData]);

  const targetPieData = useMemo(() => {
    if (!hasTargetData) return [];
    const total = Object.values(targetValues).reduce(
      (acc, cur) => acc + cur,
      0,
    );
    return Object.entries(targetValues).map(([name, value]) => {
      const originalRecord = targetRecords.find((r) => r.name === name);
      return {
        id: `target-${name}`,
        label: name,
        value,
        formattedValue: formatNumberToCurrency(value),
        percentageLabel:
          total > 0 ? convertNumberToPercentage(value / total) : '0%',
        originalRecord,
      };
    });
  }, [targetValues, hasTargetData, targetRecords]);

  const targetItems = useMemo(
    () =>
      targetPieData.map((d) => ({
        label: d.label,
        amount: d.value,
        original: d.originalRecord,
      })),
    [targetPieData],
  );

  const targetTotalValue = useMemo(() => {
    if (!hasTargetData) return 0;
    return Object.values(targetValues).reduce((acc, cur) => acc + cur, 0);
  }, [targetValues, hasTargetData]);

  const handleBarClick = (dataIndex: number) => {
    if (dataIndex < 0 || dataIndex >= portfolioRecords.length) {
      return;
    }
    const clickedRecord = portfolioRecords[dataIndex];
    if (!recordHasDiversificationDrilldown(clickedRecord)) {
      return;
    }
    const nextDiversificationId = getDrilldownDiversificationId(clickedRecord);
    const nextCategoryId = resolveDrilldownCategoryId({
      recordCategoryId: clickedRecord.CategoryId,
      currentLevelCategoryId: currentLevel.categoryId,
      categories: diversificationCategoriesData?.Records,
      diversificationId: nextDiversificationId,
      schemeCategoryId: schemeDiversificationIdToCategoryId.get(
        nextDiversificationId,
      ),
      isRootPie: false,
    });

    const sameAsCurrent =
      currentLevel.diversificationId === nextDiversificationId &&
      currentLevel.categoryId === nextCategoryId;
    if (sameAsCurrent) return;

    lastAdjustedLevelRef.current = null;
    setDrillDownStack((prev) => [
      ...prev,
      {
        type: 'bar',
        title: clickedRecord.name,
        diversificationId: nextDiversificationId,
        categoryId: nextCategoryId,
      },
    ]);
  };

  // After a bar level finishes loading: switch to holdings when every row is a leaf (no further
  // drilldown). Skip while loading/fetching so we do not decide on stale data from the prior level.
  useEffect(() => {
    if (drillDownStack.length <= 1 || isLoading || isFetching) return;
    const top = drillDownStack[drillDownStack.length - 1];
    if (top.type !== 'bar' || !portfolioRecords.length) return;

    const levelKey = `${String(top.diversificationId ?? '')}-${String(top.categoryId ?? '')}`;
    if (lastAdjustedLevelRef.current === levelKey) return;
    lastAdjustedLevelRef.current = levelKey;

    if (shouldSwitchBarLevelToHoldings(portfolioRecords)) {
      setDrillDownStack((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], type: 'holdings' };
        return next;
      });
    }
  }, [drillDownStack, portfolioRecords, isLoading, isFetching]);

  const handleBreadcrumbClick = (index: number) => {
    setDrillDownStack((prev) => prev.slice(0, index + 1));
  };

  const handleBack = () => {
    setDrillDownStack((prev) => prev.slice(0, -1));
  };

  const renderBreadcrumbs = () => (
    <Box
      mb={2}
      display='flex'
      alignItems='center'
      gap={2}
    >
      {drillDownStack.length > 1 && (
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          variant='outlined'
          size='small'
        >
          {t('back')}
        </Button>
      )}
      <Breadcrumbs separator={<NavigateNext fontSize='small' />}>
        {drillDownStack.map((level, index) => (
          <Link
            key={index}
            component='button'
            variant='body2'
            onClick={() => {
              handleBreadcrumbClick(index);
            }}
            sx={{
              cursor: index < drillDownStack.length - 1 ? 'pointer' : 'default',
              textDecoration:
                index < drillDownStack.length - 1 ? 'underline' : 'none',
              color:
                index < drillDownStack.length - 1
                  ? 'primary.main'
                  : 'text.primary',
              fontWeight:
                index === drillDownStack.length - 1 ? 'bold' : 'normal',
            }}
          >
            {level.title}
          </Link>
        ))}
      </Breadcrumbs>
    </Box>
  );

  /**
   * The error text, with a Retry button when the mount asks for one. On a
   * drilled level it keeps the breadcrumbs and Back, so a level that keeps
   * failing can still be left.
   */
  const renderError = () => {
    const message = t('assetAllocation.errorLoadingChart');
    if (!showRetry) return <Box sx={chartContentRootSx}>{message}</Box>;
    return (
      <Stack
        sx={chartContentRootSx}
        alignItems='flex-start'
        gap={1.5}
      >
        {drillDownStack.length > 1 && renderBreadcrumbs()}
        <Typography color='error'>{message}</Typography>
        <Button
          variant='outlined'
          disabled={isFetching}
          onClick={retryFailedRequests}
        >
          {t('retry', 'Retry')}
        </Button>
      </Stack>
    );
  };

  const barChartData = useMemo((): BarChartDatum[] => {
    if (currentLevel.type !== 'bar') {
      return [];
    }

    return portfolioRecords.map((item, index) => ({
      name: item.name,
      value: item.y,
      color: graphColors[index % graphColors.length],
      originalRecord: item,
    }));
  }, [portfolioRecords, currentLevel.type, graphColors]);

  if (isChartUpdating) {
    if (currentLevel.type === 'bar') {
      return (
        <Box sx={chartContentRootSx}>
          <Stack
            direction='column'
            gap={4}
          >
            {renderBreadcrumbs()}
            <BarChartSkeleton />
          </Stack>
        </Box>
      );
    }
    if (currentLevel.type === 'holdings') {
      return (
        <Box sx={chartContentRootSx}>
          <Stack
            direction='column'
            gap={4}
          >
            {renderBreadcrumbs()}
            <AssetAllocationHoldingsGrid
              records={[]}
              categoryTitle={currentLevel.title}
              graphColors={graphColors}
              isLoading
              forceMobile={shouldUseMobileLayout}
              isWidget={isWidget}
            />
          </Stack>
        </Box>
      );
    }
    return (
      <TwoPieChartsSkeleton
        forceMobile={shouldUseMobileLayout}
        variant='assetAllocation'
      />
    );
  }
  if (isError) {
    return renderError();
  }

  if (
    showNoDataMessage &&
    currentLevel.type === 'pie' &&
    currentTotalValue <= 0
  ) {
    return (
      <Box sx={chartContentRootSx}>{t('assetAllocation.noDataAvailable')}</Box>
    );
  }

  if (currentLevel.type === 'holdings') {
    return (
      <Box sx={chartContentRootSx}>
        <Stack
          direction='column'
          gap={4}
        >
          {renderBreadcrumbs()}
          <AssetAllocationHoldingsGrid
            records={portfolioRecords}
            categoryTitle={currentLevel.title}
            graphColors={graphColors}
            isLoading={false}
            endDate={asOfDateString}
            forceMobile={shouldUseMobileLayout}
            disableHoldingDetails={disableHoldingDetails}
            isWidget={isWidget}
          />
        </Stack>
      </Box>
    );
  }

  if (currentLevel.type === 'bar') {
    return (
      <Box sx={chartContentRootSx}>
        <Stack
          direction='column'
          gap={4}
        >
          {renderBreadcrumbs()}
          <Box width='100%'>
            <Typography
              variant='h6'
              mb={2}
            >
              {currentLevel.title} - {t('assetAllocation.breakdown')}
            </Typography>
            <Typography
              variant='body2'
              mb={3}
            >
              {t('dateFormats.asOfUtcDate', { date: asOfDateString })}
            </Typography>

            <Card>
              <CardContent>
                <Box height={CHART_AREA_HEIGHT}>
                  <BarChartDataContext.Provider value={barChartData}>
                    <BarChart
                      height={CHART_AREA_HEIGHT}
                      series={[
                        {
                          data: barChartData.map((item) => item.value),
                          valueFormatter: (value: number | null) =>
                            formatNumberToCurrency(value ?? 0),
                        },
                      ]}
                      slots={{
                        tooltip: AssetAllocationBarChartTooltip,
                      }}
                      xAxis={[
                        {
                          data: barChartData.map((item) => item.name),
                          scaleType: 'band',
                          tickLabelStyle: { fontSize: 12 },
                          tickLabelInterval: shouldUseMobileLayout
                            ? () => false
                            : adaptiveCategoricalTickInterval(
                                barChartData.length,
                                viewportWidth,
                              ),
                          colorMap: {
                            type: 'ordinal',
                            values: barChartData.map((i) => i.name),
                            colors: barChartData.map((i) => i.color),
                          },
                        },
                      ]}
                      yAxis={[
                        {
                          width: 80,
                          valueFormatter: (value: number) =>
                            getCurrencyStringTitleForChart(value),
                        },
                      ]}
                      margin={{
                        top: 20,
                        bottom: shouldUseMobileLayout ? 24 : 100,
                        left: 0,
                        right: 20,
                      }}
                      onItemClick={(_event, d) => {
                        if (typeof d.dataIndex === 'number') {
                          handleBarClick(d.dataIndex);
                        }
                      }}
                    />
                  </BarChartDataContext.Provider>
                </Box>

                {/* Bar chart legend/summary */}
                <Stack
                  direction='column'
                  spacing={1}
                  mt={shouldUseMobileLayout ? 1 : 3}
                >
                  {barChartData.map((item, index) => {
                    return (
                      <Box
                        key={item.name}
                        display='flex'
                        justifyContent='space-between'
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 3,
                          cursor: recordHasDiversificationDrilldown(
                            item.originalRecord,
                          )
                            ? 'pointer'
                            : 'default',
                          p: 1,
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          textAlign: 'left',
                        }}
                        onClick={() => {
                          if (
                            recordHasDiversificationDrilldown(
                              item.originalRecord,
                            )
                          ) {
                            handleBarClick(index);
                          }
                        }}
                      >
                        <Box display='flex'>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: item.color,
                              m: '6px',
                              mr: 2,
                              flexShrink: 0,
                            }}
                          />
                          <Typography>
                            {item.name}
                            {recordHasDiversificationDrilldown(
                              item.originalRecord,
                            ) && ' →'}
                          </Typography>
                        </Box>
                        <Typography
                          component='div'
                          sx={{ flexShrink: 0, textAlign: 'right' }}
                        >
                          {formatNumberToCurrency(item.value)}
                          <Typography
                            component='span'
                            color='text.secondary'
                            fontSize='small'
                            sx={{ display: 'block' }}
                          >
                            ({formatPercentValue(item.originalRecord.percent)})
                          </Typography>
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={chartContentRootSx}>
      <Stack
        direction='column'
        gap={4}
      >
        <Box
          display='flex'
          justifyContent='space-around'
          sx={{
            flexDirection: shouldUseMobileLayout
              ? 'column'
              : { xs: 'column', lg: 'row' },
            alignItems: shouldUseMobileLayout
              ? 'center'
              : { xs: 'center', lg: 'flex-start' },
            gap: shouldUseMobileLayout ? 6 : { xs: 6, lg: 4 },
          }}
          onMouseLeave={() => {
            setCurrentHighlightedItem(null);
            setTargetHighlightedItem(null);
          }}
        >
          <Box
            display='flex'
            flexDirection='column'
            flex={1}
            className='asset-allocation-current-chart'
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant='h6'>
                {t('assetAllocation.currentAllocation')}
              </Typography>
              <Typography variant='body2'>
                {t('dateFormats.asOfUtcDate', { date: asOfDateString })}
              </Typography>
              <Typography
                variant='body2'
                sx={{ mb: 2 }}
              >
                {t('assetAllocation.totalValue')}:{' '}
                {formatNumberToCurrency(currentTotalValue)}
              </Typography>
            </Box>
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
            >
              <PieChartWithLegend<DiversificationItem>
                items={currentItems}
                thresholdPercent={2}
                keepVisibleLabels={ALLOCATION_KEEP_VISIBLE_LABELS}
                width={shouldUseMobileLayout ? 300 : 350}
                height={350}
                seriesId='current'
                colorResolver={(i) => graphColors[i % graphColors.length]}
                highlightedItem={currentHighlightedItem}
                onHighlightChange={setCurrentHighlightedItem}
                onSliceClick={(item) => {
                  const originalRecord = item.original;
                  if (
                    originalRecord &&
                    recordHasDiversificationDrilldown(originalRecord) &&
                    originalRecord.id
                  ) {
                    const nextDiversificationId =
                      accountId !== undefined && currentLevel.type === 'pie'
                        ? resolveAccountRootDrilldownId(
                            originalRecord,
                            schemeRootNameToId,
                          )
                        : getDrilldownDiversificationId(originalRecord);
                    const nextCategoryId = resolveDrilldownCategoryId({
                      recordCategoryId: originalRecord.CategoryId,
                      currentLevelCategoryId: currentLevel.categoryId,
                      categories: diversificationCategoriesData?.Records,
                      diversificationId: nextDiversificationId,
                      schemeCategoryId: schemeDiversificationIdToCategoryId.get(
                        nextDiversificationId,
                      ),
                      isRootPie: currentLevel.type === 'pie',
                    });
                    setDrillDownStack((prev) => [
                      ...prev,
                      {
                        type: 'bar',
                        title: item.label,
                        diversificationId: nextDiversificationId,
                        categoryId: nextCategoryId,
                      },
                    ]);
                  }
                }}
              />
            </Box>
          </Box>

          {showTarget && (
            <Box
              display='flex'
              flexDirection='column'
              textAlign='center'
              flex={1}
              sx={{
                px: shouldUseMobileLayout ? 2 : { xs: 2, lg: 0 },
              }}
            >
              <Typography variant='h6'>
                {t('assetAllocation.targetAllocation')}
              </Typography>
              <Typography variant='body2'>
                {t('assetAllocation.comparisonTarget')}
              </Typography>
              <Typography
                variant='body2'
                sx={{ mb: 2 }}
              >
                {t('assetAllocation.totalTargetValue')}:{' '}
                {formatNumberToCurrency(targetTotalValue)}
              </Typography>
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
              >
                <PieChartWithLegend<DiversificationItem>
                  items={targetItems}
                  thresholdPercent={2}
                  keepVisibleLabels={ALLOCATION_KEEP_VISIBLE_LABELS}
                  width={shouldUseMobileLayout ? 300 : 350}
                  height={350}
                  seriesId='target'
                  colorResolver={(i) => graphColors[i % graphColors.length]}
                  highlightedItem={targetHighlightedItem}
                  onHighlightChange={setTargetHighlightedItem}
                  onSliceClick={(item) => {
                    const originalRecord = item.original;
                    if (
                      originalRecord &&
                      recordHasDiversificationDrilldown(originalRecord) &&
                      originalRecord.id
                    ) {
                      const nextDiversificationId =
                        accountId !== undefined && currentLevel.type === 'pie'
                          ? resolveAccountRootDrilldownId(
                              originalRecord,
                              schemeRootNameToId,
                            )
                          : getDrilldownDiversificationId(originalRecord);
                      const nextCategoryId = resolveDrilldownCategoryId({
                        recordCategoryId: originalRecord.CategoryId,
                        currentLevelCategoryId: currentLevel.categoryId,
                        categories: diversificationCategoriesData?.Records,
                        diversificationId: nextDiversificationId,
                        schemeCategoryId:
                          schemeDiversificationIdToCategoryId.get(
                            nextDiversificationId,
                          ),
                        isRootPie: currentLevel.type === 'pie',
                      });
                      setDrillDownStack((prev) => [
                        ...prev,
                        {
                          type: 'bar',
                          title: item.label,
                          diversificationId: nextDiversificationId,
                          categoryId: nextCategoryId,
                        },
                      ]);
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default AssetAllocationPieChart;
